import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CargoRequestService, CreateCargoRequest, CargoRequest } from '../../../services/api/cargo-request';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { FileService } from '../../../services/api/file';
import { CustomsCalculationService, CreateCustomsProductRequest } from '../../../services/api/customs-calculation';
import { CurrencyRatesService } from '../../../services/currency-rates';
import { CountrySelectComponent } from '../../shared/country-select/country-select';
import { Subscription, forkJoin, debounceTime } from 'rxjs';

const CUSTOMS_CURRENCIES = ['USD', 'EUR', 'CNY'] as const;

interface CargoTypeDto {
  id: number;
  nameRu: string;
  nameEn: string;
}

interface CurrencyDto {
  id: number;
  nameRu: string;
  nameEn: string;
  code: string;
  symbol: string;
}

@Component({
  selector: 'app-create-request',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, CountrySelectComponent],
  templateUrl: './create-request.html',
  styleUrl: './create-request.scss'
})
export class CreateRequestComponent implements OnInit, OnDestroy {
  /** ID уже существующего независимого расчёта для привязки (сценарий «Обработать»).
   *  Когда задан — компонент считается встроенным в вкладку «Перевозка» страницы обработки
   *  расчёта: после успешного создания эмитит requestCreated вместо навигации на дашборд. */
  @Input() customsCalculationId?: number;
  @Output() requestCreated = new EventEmitter<void>();

  requestForm: FormGroup;
  countries: CountryDto[] = [];
  cargoTypes: CargoTypeDto[] = [];
  currencies: CurrencyDto[] = [];
  loading = false;
  submitting = false;
  error: string | null = null;
  private _success = false;
  /** Сеттер централизованно чистит черновик из хранилища при успехе — вместо
   *  того чтобы дёргать clearDraft() в каждой из veсьма многочисленных веток onSubmit(). */
  get success(): boolean {
    return this._success;
  }
  set success(value: boolean) {
    this._success = value;
    if (value) {
      this.clearDraft();
    }
  }
  isEditMode = false;
  requestId: number | null = null;
  canEditCustomsProducts = true; // Можно редактировать товары таможенных платежей, если брокер не начал работу
  existingCustomsProducts: any[] = []; // Существующие товары для расчета таможенных платежей (при редактировании)

  // Translations
  titleText = 'Создать запрос на перевозку';
  rateTypeText = 'Тип ставки';
  rateTypeOptions: Array<{ value: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER'; label: string }> = [];
  routeText = 'Маршрут';
  fromText = 'Из';
  toText = 'В';
  fromCountryText = 'Страна';
  fromCityText = 'Город';
  toCountryText = 'Страна';
  toCityText = 'Город';
  loadingDateText = 'Дата погрузки';
  priceText = 'Цена';
  cargoCostText = 'Стоимость груза';
  currencyText = 'Валюта';
  calculateCustomsText = 'Рассчитать таможенные платежи';
  productsText = 'Грузовые места';
  addProductText = 'Добавить грузовое место';
  removeProductText = 'Удалить';
  cargoTypeText = 'Тип груза';
  isDangerousText = 'Опасный груз';
  isOversizedText = 'Крупногабаритный';
  nameText = 'Наименование';
  tnvedCodeText = 'Код ТНВЭД';
  tnvedHintText = 'Определить код ТНВЭД';
  readonly tnvedHintUrl = '/cargo-owner/dashboard/calculations';
  quantityText = 'Количество';
  weightText = 'Вес (кг)';
  totalWeightText = 'Суммарный вес (кг)';
  totalVolumeText = 'Суммарный объем (м³)';
  totalWeightCalculatedText = 'Общий вес (вычислено) кг';
  totalVolumeCalculatedText = 'Общий объем (м³) (вычислено)';
  dimensionsText = 'Габариты';
  volumeText = 'Объем';
  useDimensionsText = 'Заполнить вес и габариты груза';
  useVolumeText = 'Заполнить суммарные данные груза';
  lengthText = 'Длина (мм)';
  widthText = 'Ширина (мм)';
  heightText = 'Высота (мм)';
  volumeM3Text = 'Объем (м³)';
  isNonStackableText = 'Не штабелируется';
  submitText = 'Создать запрос';
  saveAsDraftText = 'Сохранить как черновик';
  submittingText = 'Создание...';
  cancelText = 'Отмена';
  requiredText = '*';

  // Товары для расчета таможенных платежей
  customsProductsText = 'Товары для расчета таможенных платежей';
  addCustomsProductText = 'Добавить товар';
  customsProductText = 'Товар';
  customsProductNameText = 'Наименование товара';
  customsProductTnvedText = 'Код ТНВЭД';
  customsProductCostText = 'Стоимость товара';
  applicationScopeHintText = 'Кратко опишите товар: что это, для чего используется, из чего изготовлен, как работает, основные характеристики. Информация о товаре нужна для корректного расчета через AI.';
  customsCurrencyText = 'Валюта';
  invoiceText = 'Инвойс';
  attachInvoiceText = 'Прикрепить инвойс';
  removeText = 'Удалить';
  brokerStartedWorkText = 'Брокер уже начал работу над расчетом. Редактирование товаров недоступно.';
  maxCustomsProductsHintText = 'Максимум 3 товара для растаможки';

  mainInfoTitleText = 'Основная информация';
  requiredFieldText = 'Обязательное поле';
  requiredFieldMinOneText = 'Обязательное поле (минимум 1)';
  requiredFieldMinVolumeText = 'Обязательное поле (минимум 0.0001 м³)';
  autoFilledPlaceholderText = 'Заполняется автоматически';
  cargoPlaceLabelText = 'Грузовое место';
  fillAllDimensionsWarningText = '⚠️ Необходимо заполнить все габариты (длина, ширина, высота)';
  lengthShortLabelText = 'Длина';
  widthShortLabelText = 'Ширина';
  heightShortLabelText = 'Высота';
  notFilledText = 'не заполнено';
  fillTotalVolumeWarningText = '⚠️ Необходимо заполнить суммарный объем';
  fillTotalWeightWarningText = '⚠️ Необходимо заполнить суммарный вес';
  requestCreatedSuccessText = 'Запрос успешно создан! Перенаправление...';
  continueWithOwnForwarderText = 'Продолжить со своим экспедитором';

  // Инвойс
  invoiceFile: File | null = null;
  invoiceFileName: string = '';
  invoiceError: string | null = null;
  hasExistingInvoice: boolean = false; // Флаг, что инвойс уже загружен на сервере
  existingInvoiceFileName: string = ''; // Имя существующего инвойса для отображения

  // AI расчет
  calculatingWithAi: { [key: number]: boolean } = {}; // Индекс товара -> состояние загрузки

  rates: Record<string, number> = { USD: 501, EUR: 597, CNY: 72 };
  readonly customsCurrencies = CUSTOMS_CURRENCIES;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private cargoRequestService: CargoRequestService,
    private dictionaryService: DictionaryService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private fileService: FileService,
    private customsCalculationService: CustomsCalculationService,
    private currencyRates: CurrencyRatesService
  ) {
    this.requestForm = this.fb.group({
      rateType: ['FTL', Validators.required],
      fromCountryId: [null, Validators.required],
      fromCity: ['', Validators.required],
      toCountryId: [null, Validators.required],
      toCity: ['', Validators.required],
      loadingDate: ['', Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      currencyId: [null, Validators.required],
      calculateCustomsPayments: [false],
      continueWithOwnForwarder: [false],
      products: this.fb.array([this.createProductFormGroup()]),
      customsProducts: this.fb.array([])
    });
  }

  ngOnInit(): void {
    // Проверяем аутентификацию перед загрузкой данных
    if (!this.authService.isAuthenticated()) {
      console.warn('User not authenticated, redirecting to login');
      this.router.navigate(['/login']);
      return;
    }

    // customsCalculationId может прийти как @Input (встроенный режим) или как query-параметр
    // (прямая навигация со страницы обработки расчёта)
    if (!this.customsCalculationId) {
      const calcId = this.route.snapshot.queryParamMap.get('customsCalculationId');
      if (calcId) {
        this.customsCalculationId = +calcId;
      }
    }

    // Проверяем, находимся ли мы в режиме редактирования. Пропускаем эту проверку во
    // встроенном режиме (customsCalculationId задан) — там нет собственного маршрута,
    // ActivatedRoute.paramMap наследуется от родителя (/calculations/:id/process),
    // и ":id" там — это ID расчёта, а не заявки, режим редактирования тут неприменим.
    if (!this.customsCalculationId) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditMode = true;
        this.requestId = +id;
        this.titleText = 'Редактировать запрос на перевозку';
        this.submitText = 'Сохранить изменения';
        this.submittingText = 'Сохранение...';
        this.loading = true;
      }
    }

    this.loadDictionaries();
    this.updateTranslations();

    // Если режим редактирования, загружаем данные запроса
    if (this.isEditMode && this.requestId) {
      this.loadRequestForEdit(this.requestId);
    } else {
      // Черновик незавершённой формы — переживает уход со страницы (например, по ссылке
      // "Чат" или "К расчёту" и обратно) и живёт до успешной отправки или закрытия вкладки.
      this.restoreDraft();
      this.subscriptions.add(
        this.requestForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.saveDraft())
      );
    }

    this.subscriptions.add(
      this.currencyRates.getRates().subscribe((r) => {
        this.rates = { ...r };
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
      })
    );

    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(() => {
        this.loadDictionaries();
      })
    );

    // Подписываемся на изменения формы для обновления состояния кнопки
    this.subscriptions.add(
      this.requestForm.valueChanges.subscribe(() => {
        // Обновляем валидность формы при изменениях
        this.requestForm.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      })
    );

    // Подписываемся на изменения статуса валидности формы
    this.subscriptions.add(
      this.requestForm.statusChanges.subscribe(() => {
        this.cdr.markForCheck();
      })
    );

    // Подписываемся на изменения товаров таможенных платежей
    this.subscriptions.add(
      this.customsProducts.valueChanges.subscribe(() => {
        // Обновляем валидность каждого товара
        this.customsProducts.controls.forEach(control => {
          if (control instanceof FormGroup) {
            control.updateValueAndValidity({ emitEvent: false });
          }
        });
        this.requestForm.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      })
    );

    // Подписываемся на изменения статуса валидности товаров таможенных платежей
    this.customsProducts.controls.forEach(control => {
      if (control instanceof FormGroup) {
        this.subscriptions.add(
          control.statusChanges.subscribe(() => {
            this.cdr.markForCheck();
          })
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Уход с формы (другой раздел, вкладка «Поставщик» и т.п.) — сохраняем сразу, не дожидаясь паузы после ввода
    if (!this.isEditMode && !this.success) {
      this.saveDraft();
    }
  }

  /** Отдельный ключ на каждый расчёт (встроенный режим) и один общий — на обычное
   *  создание заявки "с нуля", чтобы черновики разных контекстов не перетирали друг друга. */
  private get draftStorageKey(): string {
    return this.customsCalculationId
      ? `createRequestDraft:calc:${this.customsCalculationId}`
      : 'createRequestDraft:standalone';
  }

  /** Черновик живёт неделю: в localStorage, чтобы переживать и закрытие вкладки, и открытие расчёта в другой вкладке. */
  private static readonly DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  private restoreDraft(): void {
    let raw: string | null = null;
    try {
      // Старые черновики лежали в sessionStorage — подхватываем и их
      raw = localStorage.getItem(this.draftStorageKey) ?? sessionStorage.getItem(this.draftStorageKey);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const saved = JSON.parse(raw);
      const draft = saved && saved.form ? saved.form : saved;
      if (saved?.savedAt && Date.now() - saved.savedAt > CreateRequestComponent.DRAFT_TTL_MS) {
        this.clearDraft();
        return;
      }
      this.resizeFormArray(this.products, Array.isArray(draft.products) ? draft.products.length : 1, () => this.createProductFormGroup());
      this.resizeFormArray(this.customsProducts, Array.isArray(draft.customsProducts) ? draft.customsProducts.length : 0, () => this.createCustomsProductFormGroup());
      this.requestForm.patchValue(draft, { emitEvent: false });
      this.cdr.markForCheck();
    } catch (e) {
      console.warn('Не удалось восстановить черновик заявки:', e);
      this.clearDraft();
    }
  }

  private resizeFormArray(array: FormArray, targetLength: number, createGroup: () => FormGroup): void {
    while (array.length < targetLength) {
      array.push(createGroup());
    }
    while (array.length > targetLength) {
      array.removeAt(array.length - 1);
    }
  }

  private saveDraft(): void {
    try {
      localStorage.setItem(this.draftStorageKey, JSON.stringify({ savedAt: Date.now(), form: this.requestForm.value }));
    } catch {
      // Хранилище недоступно (приватный режим браузера и т.п.) — черновик просто не сохранится.
    }
  }

  private clearDraft(): void {
    try {
      localStorage.removeItem(this.draftStorageKey);
      sessionStorage.removeItem(this.draftStorageKey);
    } catch {
      // ignore
    }
  }

  private updateTranslations(): void {
    if (!this.isEditMode) {
      this.titleText = this.languageService.translate('cargoOwner.createRequest.title') || 'Создать запрос на перевозку';
      this.submitText = this.languageService.translate('cargoOwner.createRequest.submit') || 'Создать запрос';
      this.submittingText = this.languageService.translate('cargoOwner.createRequest.submitting') || 'Создание...';
    }
    this.rateTypeText = this.languageService.translate('cargoOwner.createRequest.rateType') || 'Тип ставки';
    this.rateTypeOptions = [
      { value: 'FTL', label: this.languageService.translate('cargoOwner.createRequest.rateTypeFTL') || 'FTL (Выделенный АТС)' },
      { value: 'LTL', label: this.languageService.translate('cargoOwner.createRequest.rateTypeLTL') || 'LTL (Сборный груз)' },
      { value: 'AIR', label: this.languageService.translate('cargoOwner.createRequest.rateTypeAIR') || 'Авиа' },
      { value: 'CONTAINER', label: this.languageService.translate('cargoOwner.createRequest.rateTypeCONTAINER') || 'Контейнер' }
    ];
    this.cargoCostText = this.languageService.translate('cargoOwner.createRequest.cargoCost') || 'Стоимость груза';
    this.fromCountryText = this.languageService.translate('cargoOwner.createRequest.fromCountry') || 'Страна отправления';
    this.fromCityText = this.languageService.translate('cargoOwner.createRequest.fromCity') || 'Город отправления';
    this.toCountryText = this.languageService.translate('cargoOwner.createRequest.toCountry') || 'Страна назначения';
    this.toCityText = this.languageService.translate('cargoOwner.createRequest.toCity') || 'Город назначения';
    this.loadingDateText = this.languageService.translate('cargoOwner.createRequest.loadingDate') || 'Дата погрузки';
    this.priceText = this.languageService.translate('cargoOwner.createRequest.price') || 'Цена';
    this.currencyText = this.languageService.translate('cargoOwner.createRequest.currency') || 'Валюта';
    this.calculateCustomsText = this.languageService.translate('cargoOwner.createRequest.calculateCustoms') || 'Рассчитать таможенные платежи';
    this.productsText = this.languageService.translate('cargoOwner.createRequest.products') || 'Грузовые места';
    this.addProductText = this.languageService.translate('cargoOwner.createRequest.addProduct') || 'Добавить грузовое место';
    this.removeProductText = this.languageService.translate('cargoOwner.createRequest.removeProduct') || 'Удалить';
    this.cargoTypeText = this.languageService.translate('cargoOwner.createRequest.cargoType') || 'Тип груза';
    this.isDangerousText = this.languageService.translate('cargoOwner.createRequest.isDangerous') || 'Опасный груз';
    this.isOversizedText = this.languageService.translate('cargoOwner.createRequest.isOversized') || 'Крупногабаритный';
    this.nameText = this.languageService.translate('cargoOwner.createRequest.productName') || 'Наименование';
    this.tnvedCodeText = this.languageService.translate('cargoOwner.createRequest.tnvedCode') || 'Код ТНВЭД';
    this.tnvedHintText = this.languageService.translate('cargoOwner.createRequest.tnvedHint') || 'Определить код ТНВЭД';
    this.quantityText = this.languageService.translate('cargoOwner.createRequest.quantity') || 'Количество';
    this.weightText = this.languageService.translate('cargoOwner.createRequest.weight') || 'Вес (кг)';
    this.totalWeightText = this.languageService.translate('cargoOwner.createRequest.totalWeight') || 'Суммарный вес (кг)';
    this.totalVolumeText = this.languageService.translate('cargoOwner.createRequest.totalVolume') || 'Суммарный объем (м³)';
    this.totalWeightCalculatedText = this.languageService.translate('cargoOwner.createRequest.totalWeightCalculated') || 'Общий вес (вычислено) кг';
    this.totalVolumeCalculatedText = this.languageService.translate('cargoOwner.createRequest.totalVolumeCalculated') || 'Общий объем (м³) (вычислено)';
    this.dimensionsText = this.languageService.translate('cargoOwner.createRequest.dimensions') || 'Габариты';
    this.volumeText = this.languageService.translate('cargoOwner.createRequest.volume') || 'Объем';
    this.useDimensionsText = this.languageService.translate('cargoOwner.createRequest.useDimensions') || 'Заполнить вес и габариты груза';
    this.useVolumeText = this.languageService.translate('cargoOwner.createRequest.useVolume') || 'Заполнить суммарные данные груза';
    this.lengthText = this.languageService.translate('cargoOwner.createRequest.length') || 'Длина (мм)';
    this.widthText = this.languageService.translate('cargoOwner.createRequest.width') || 'Ширина (мм)';
    this.heightText = this.languageService.translate('cargoOwner.createRequest.height') || 'Высота (мм)';
    this.volumeM3Text = this.languageService.translate('cargoOwner.createRequest.volumeM3') || 'Объем (м³)';
    this.isNonStackableText = this.languageService.translate('cargoOwner.createRequest.isNonStackable') || 'Не штабелируется';
    if (!this.isEditMode) {
      this.submitText = this.languageService.translate('cargoOwner.createRequest.submit') || 'Создать запрос';
      this.submittingText = this.languageService.translate('cargoOwner.createRequest.submitting') || 'Создание...';
    }
    this.saveAsDraftText = this.languageService.translate('cargoOwner.createRequest.saveAsDraft') || 'Сохранить как черновик';
    this.cancelText = this.languageService.translate('cargoOwner.createRequest.cancel') || 'Отмена';
    this.requiredText = this.languageService.translate('common.required') || '*';
    
    // Товары для расчета таможенных платежей
    this.customsProductsText = this.languageService.translate('cargoOwner.createRequest.customsProducts') || 'Товары для расчета таможенных платежей';
    this.addCustomsProductText = this.languageService.translate('cargoOwner.createRequest.addCustomsProduct') || 'Добавить товар';
    this.customsProductText = this.languageService.translate('cargoOwner.createRequest.customsProduct') || 'Товар';
    this.customsProductNameText = this.languageService.translate('cargoOwner.createRequest.customsProductName') || 'Наименование товара';
    this.customsProductTnvedText = this.languageService.translate('cargoOwner.createRequest.customsProductTnved') || 'Код ТНВЭД';
    this.customsProductCostText = this.languageService.translate('cargoOwner.createRequest.customsProductCost') || 'Стоимость товара';
    this.applicationScopeHintText = this.languageService.translate('cargoOwner.createRequest.applicationScopeHint') || 'Кратко опишите товар: что это, для чего используется, из чего изготовлен, как работает, основные характеристики. Информация о товаре нужна для корректного расчета через AI.';
    this.invoiceText = this.languageService.translate('cargoOwner.createRequest.invoice') || 'Инвойс';
    this.attachInvoiceText = this.languageService.translate('cargoOwner.createRequest.attachInvoice') || 'Прикрепить инвойс';
    this.removeText = this.languageService.translate('cargoOwner.createRequest.remove') || 'Удалить';
    this.brokerStartedWorkText = this.languageService.translate('cargoOwner.createRequest.brokerStartedWork') || 'Брокер уже начал работу над расчетом. Редактирование товаров недоступно.';
    this.maxCustomsProductsHintText = this.languageService.translate('cargoOwner.createRequest.maxCustomsProductsHint') || 'Максимум 3 товара для растаможки';

    this.mainInfoTitleText = this.languageService.translate('cargoOwner.createRequest.mainInfoTitle') || this.mainInfoTitleText;
    this.requiredFieldText = this.languageService.translate('cargoOwner.createRequest.requiredField') || this.requiredFieldText;
    this.requiredFieldMinOneText = this.languageService.translate('cargoOwner.createRequest.requiredFieldMinOne') || this.requiredFieldMinOneText;
    this.requiredFieldMinVolumeText = this.languageService.translate('cargoOwner.createRequest.requiredFieldMinVolume') || this.requiredFieldMinVolumeText;
    this.autoFilledPlaceholderText = this.languageService.translate('cargoOwner.createRequest.autoFilledPlaceholder') || this.autoFilledPlaceholderText;
    this.cargoPlaceLabelText = this.languageService.translate('cargoOwner.createRequest.cargoPlaceLabel') || this.cargoPlaceLabelText;
    this.fillAllDimensionsWarningText = this.languageService.translate('cargoOwner.createRequest.fillAllDimensionsWarning') || this.fillAllDimensionsWarningText;
    this.lengthShortLabelText = this.languageService.translate('cargoOwner.createRequest.lengthShortLabel') || this.lengthShortLabelText;
    this.widthShortLabelText = this.languageService.translate('cargoOwner.createRequest.widthShortLabel') || this.widthShortLabelText;
    this.heightShortLabelText = this.languageService.translate('cargoOwner.createRequest.heightShortLabel') || this.heightShortLabelText;
    this.notFilledText = this.languageService.translate('cargoOwner.createRequest.notFilled') || this.notFilledText;
    this.fillTotalVolumeWarningText = this.languageService.translate('cargoOwner.createRequest.fillTotalVolumeWarning') || this.fillTotalVolumeWarningText;
    this.fillTotalWeightWarningText = this.languageService.translate('cargoOwner.createRequest.fillTotalWeightWarning') || this.fillTotalWeightWarningText;
    this.requestCreatedSuccessText = this.languageService.translate('cargoOwner.createRequest.requestCreatedSuccess') || this.requestCreatedSuccessText;
    this.continueWithOwnForwarderText = this.languageService.translate('cargoOwner.createRequest.continueWithOwnForwarder') || this.continueWithOwnForwarderText;

    this.cdr.markForCheck();
  }

  private loadDictionaries(): void {
    this.loading = true;

    const countries$ = this.dictionaryService.getAllCountries();
    const cargoTypes$ = this.dictionaryService.getCargoTypes();
    const currencies$ = this.dictionaryService.getCurrencies();

    this.subscriptions.add(
      countries$.subscribe({
        next: (countries) => {
          this.countries = countries;
          this.checkLoadingComplete();
        },
        error: (err) => {
          console.error('Error loading countries:', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа. Пожалуйста, войдите в систему.';
          }
          this.checkLoadingComplete();
        }
      })
    );

    this.subscriptions.add(
      cargoTypes$.subscribe({
        next: (types) => {
          this.cargoTypes = types;
          this.checkLoadingComplete();
        },
        error: (err) => {
          console.error('Error loading cargo types:', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа. Пожалуйста, войдите в систему.';
          }
          this.checkLoadingComplete();
        }
      })
    );

    this.subscriptions.add(
      currencies$.subscribe({
        next: (currencies) => {
          this.currencies = currencies;
          this.checkLoadingComplete();
        },
        error: (err) => {
          console.error('Error loading currencies:', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа. Пожалуйста, войдите в систему.';
          }
          this.checkLoadingComplete();
        }
      })
    );
  }

  private checkLoadingComplete(): void {
    if (this.countries.length > 0 && this.cargoTypes.length > 0 && this.currencies.length > 0) {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  get products(): FormArray {
    return this.requestForm.get('products') as FormArray;
  }

  get customsProducts(): FormArray {
    return this.requestForm.get('customsProducts') as FormArray;
  }

  /**
   * Проверяет, можно ли отправить форму (включая валидацию товаров таможенных платежей и инвойса)
   */
  get canSubmit(): boolean {
    const log: string[] = [];
    
    // Проверяем валидность основной формы
    if (this.requestForm.invalid) {
      const invalidFields: string[] = [];
      Object.keys(this.requestForm.controls).forEach(key => {
        const control = this.requestForm.get(key);
        if (control && control.invalid) {
          if (control instanceof FormArray) {
            // Для FormArray проверяем каждый элемент
            const invalidItems: string[] = [];
            control.controls.forEach((item, index) => {
              if (item.invalid) {
                if (item instanceof FormGroup) {
                  const itemInvalidFields: string[] = [];
                  Object.keys(item.controls).forEach(itemKey => {
                    const itemControl = item.get(itemKey);
                    if (itemControl && itemControl.invalid) {
                      itemInvalidFields.push(`${itemKey} (${JSON.stringify(itemControl.errors)})`);
                    }
                  });
                  // Если есть ошибки на уровне группы (валидатор группы)
                  if (item.errors && Object.keys(item.errors).length > 0) {
                    itemInvalidFields.push(`groupErrors: ${JSON.stringify(item.errors)}`);
                  }
                  const fieldsStr = itemInvalidFields.length > 0 
                    ? itemInvalidFields.join(', ') 
                    : 'invalid (no field errors, check group validator)';
                  invalidItems.push(`item[${index}]: ${fieldsStr}`);
                } else {
                  invalidItems.push(`item[${index}]: invalid (errors: ${JSON.stringify(item.errors)})`);
                }
              }
            });
            if (invalidItems.length > 0) {
              invalidFields.push(`${key} (FormArray invalid - ${invalidItems.join('; ')})`);
            } else {
              invalidFields.push(`${key} (FormArray invalid but no invalid items found)`);
            }
          } else {
            invalidFields.push(`${key} (errors: ${JSON.stringify(control.errors)})`);
          }
        }
      });
      log.push(`canSubmit: form invalid - fields: ${invalidFields.join(', ')}`);
      console.log(log.join('\n'));
      return false;
    }
    log.push('canSubmit: form valid');

    // Если требуется расчет таможенных платежей, проверяем товары и инвойс
    const calculateCustoms = this.requestForm.get('calculateCustomsPayments')?.value;
    if (calculateCustoms) {
      log.push(`canSubmit: calculateCustomsPayments = ${calculateCustoms}`);
      
      // Проверяем, что есть хотя бы один товар
      if (this.customsProducts.length === 0) {
        log.push('canSubmit: no customs products');
        console.log(log.join('\n'));
        return false;
      }
      log.push(`canSubmit: customsProducts count = ${this.customsProducts.length}`);

      // Проверяем валидность всех товаров
      for (let i = 0; i < this.customsProducts.controls.length; i++) {
        const productGroup = this.customsProducts.controls[i];
        if (productGroup instanceof FormGroup) {
          if (productGroup.invalid) {
            const invalidFields: string[] = [];
            Object.keys(productGroup.controls).forEach(key => {
              const control = productGroup.get(key);
              if (control && control.invalid) {
                invalidFields.push(`${key} (errors: ${JSON.stringify(control.errors)})`);
              }
            });
            log.push(`canSubmit: product ${i + 1} invalid - fields: ${invalidFields.join(', ')}`);
            console.log(log.join('\n'));
            return false;
          }
        }
      }
      log.push('canSubmit: all customs products valid');

      // Проверяем наличие инвойса (новый файл или уже существующий на сервере)
      const hasInvoice = this.invoiceFile || this.hasExistingInvoice;
      log.push(`canSubmit: invoice check - invoiceFile: ${!!this.invoiceFile}, hasExistingInvoice: ${this.hasExistingInvoice}, hasInvoice: ${hasInvoice}`);
      if (!hasInvoice) {
        log.push('canSubmit: no invoice');
        console.log(log.join('\n'));
        return false;
      }
    } else {
      log.push(`canSubmit: calculateCustomsPayments = ${calculateCustoms} (skipping customs validation)`);
    }

    log.push('canSubmit: ✅ ALL CHECKS PASSED');
    console.log(log.join('\n'));
    return true;
  }

  createProductFormGroup(): FormGroup {
    const group = this.fb.group({
      cargoTypeId: [null, Validators.required],
      name: ['', Validators.required],
      tnvedCode: ['', Validators.required],
      quantity: [1, [Validators.min(1)]],
      weightKg: [null, [Validators.min(0.01)]],
      isDangerous: [false],
      isOversized: [false],
      totalWeightKg: [null, [Validators.min(0.01)]], // Используется только в режиме объема
      useDimensions: [true], // true = вес и габариты, false = суммарные данные
      lengthMm: [null, [Validators.min(1)]],
      widthMm: [null, [Validators.min(1)]],
      heightMm: [null, [Validators.min(1)]],
      volumeM3: [null],
      isNonStackable: [false]
    });

    // Добавляем валидатор для проверки: либо габариты, либо объем
    group.setValidators(this.dimensionsOrVolumeValidator);
    
    // Подписываемся на изменения для автовычисления объема
    group.get('lengthMm')?.valueChanges.subscribe(() => this.calculateVolume(group));
    group.get('widthMm')?.valueChanges.subscribe(() => this.calculateVolume(group));
    group.get('heightMm')?.valueChanges.subscribe(() => this.calculateVolume(group));
    // При изменении количества или веса — обновить отображение вычисленных полей
    group.get('quantity')?.valueChanges.subscribe(() => this.cdr.markForCheck());
    group.get('weightKg')?.valueChanges.subscribe(() => this.cdr.markForCheck());
    group.get('useDimensions')?.valueChanges.subscribe((useDimensions) => {
      // Правильно обрабатываем значение: может быть true, false, "true", "false"
      let useDims: boolean;
      if (useDimensions === true) {
        useDims = true;
      } else if (useDimensions === false) {
        useDims = false;
      } else if (typeof useDimensions === 'string') {
        // Обрабатываем строковые значения
        useDims = useDimensions === 'true';
      } else {
        useDims = Boolean(useDimensions);
      }
      
      this.updateDimensionsOrVolumeValidation(group, useDims);

      if (useDims) {
        group.get('volumeM3')?.setValue(null, { emitEvent: false });
        group.get('totalWeightKg')?.setValue(null, { emitEvent: false });
        setTimeout(() => this.calculateVolume(group), 0);
      } else {
        group.get('lengthMm')?.setValue(null, { emitEvent: false });
        group.get('widthMm')?.setValue(null, { emitEvent: false });
        group.get('heightMm')?.setValue(null, { emitEvent: false });
        group.get('weightKg')?.setValue(null, { emitEvent: false });
        group.get('quantity')?.setValue(1, { emitEvent: false });
        setTimeout(() => {
          group.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
          group.get('totalWeightKg')?.updateValueAndValidity({ emitEvent: false });
          group.updateValueAndValidity({ emitEvent: false });
          this.cdr.markForCheck();
        }, 0);
      }
    });
    
    // Подписываемся на изменения volumeM3 для пересчета валидации группы
    group.get('volumeM3')?.valueChanges.subscribe(() => {
      // Пересчитываем валидацию группы при изменении объема
      // Используем несколько попыток для надежности
      setTimeout(() => {
        // Сначала обновляем валидацию самого поля
        group.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
        // Затем обновляем валидацию всей группы (где находится dimensionsOrVolumeValidator)
        group.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      }, 0);
      setTimeout(() => {
        group.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      }, 50);
    });

    // Вычисляем объем при инициализации, если габариты уже заполнены
    setTimeout(() => {
      if (Boolean(group.get('useDimensions')?.value)) {
        this.calculateVolume(group);
      }
    }, 0);

    return group;
  }

  /**
   * Валидатор: либо заполнены все габариты, либо объем
   */
  private dimensionsOrVolumeValidator: ValidatorFn = (control: AbstractControl) => {
    if (!(control instanceof FormGroup)) {
      return null;
    }
    
    const group = control as FormGroup;
    const useDimensionsValue = group.get('useDimensions')?.value;
    // Правильно обрабатываем значение: может быть true, false, "true", "false", null, undefined
    let useDimensions: boolean;
    if (useDimensionsValue === true) {
      useDimensions = true;
    } else if (useDimensionsValue === false) {
      useDimensions = false;
    } else if (typeof useDimensionsValue === 'string') {
      // Обрабатываем строковые значения
      useDimensions = useDimensionsValue === 'true';
    } else {
      // По умолчанию используем габариты
      useDimensions = true;
    }
    
    if (useDimensions) {
      // Режим "Заполнить вес и габариты": вес, габариты, количество обязательны
      const weightKg = group.get('weightKg')?.value;
      const quantity = group.get('quantity')?.value;
      const length = group.get('lengthMm')?.value;
      const width = group.get('widthMm')?.value;
      const height = group.get('heightMm')?.value;

      const weightNum = weightKg != null && weightKg !== '' ? Number(weightKg) : null;
      const qtyNum = quantity != null && quantity !== '' ? Number(quantity) : null;
      const lengthNum = length != null && length !== '' ? Number(length) : null;
      const widthNum = width != null && width !== '' ? Number(width) : null;
      const heightNum = height != null && height !== '' ? Number(height) : null;

      const hasWeight = weightNum != null && !isNaN(weightNum) && weightNum > 0;
      const hasQuantity = qtyNum != null && !isNaN(qtyNum) && qtyNum >= 1;
      const hasLength = lengthNum != null && !isNaN(lengthNum) && lengthNum > 0;
      const hasWidth = widthNum != null && !isNaN(widthNum) && widthNum > 0;
      const hasHeight = heightNum != null && !isNaN(heightNum) && heightNum > 0;

      if (!hasWeight) return { weightRequired: true };
      if (!hasQuantity) return { quantityRequired: true };
      if (!hasLength || !hasWidth || !hasHeight) return { dimensionsRequired: true };
    } else {
      // Режим "Заполнить суммарные данные": объем и суммарный вес обязательны
      const totalWeightKg = group.get('totalWeightKg')?.value;
      const volumeControl = group.get('volumeM3');
      const volume = volumeControl?.value;

      const totalWeightNum = totalWeightKg != null && totalWeightKg !== '' ? Number(totalWeightKg) : null;
      const hasTotalWeight = totalWeightNum != null && !isNaN(totalWeightNum) && totalWeightNum > 0;
      if (!hasTotalWeight) return { totalWeightRequired: true };
      
      // Проверяем, что объем заполнен и больше 0
      // Преобразуем в число для корректной проверки (обрабатываем строки, числа, null, undefined)
      let volumeNum: number | null = null;
      
      // Обрабатываем различные типы значений
      if (volume != null && volume !== '') {
        if (typeof volume === 'number') {
          volumeNum = volume;
        } else if (typeof volume === 'string') {
          const trimmed = volume.trim();
          if (trimmed !== '') {
            const parsed = Number(trimmed);
            if (!isNaN(parsed) && isFinite(parsed)) {
              volumeNum = parsed;
            }
          }
        }
      }
      
      const hasVolume = volumeNum != null && volumeNum > 0;
      if (!hasVolume) return { volumeRequired: true };
      // Это вызывает бесконечную рекурсию
    }
    
    return null;
  };

  /**
   * Обновление валидации при переключении между габаритами и объемом
   */
  private updateDimensionsOrVolumeValidation(group: FormGroup, useDimensions: boolean): void {
    
    if (useDimensions) {
      group.get('lengthMm')?.setValidators([Validators.required, Validators.min(1)]);
      group.get('widthMm')?.setValidators([Validators.required, Validators.min(1)]);
      group.get('heightMm')?.setValidators([Validators.required, Validators.min(1)]);
      // Для режима габаритов volumeM3 не валидируем через стандартные валидаторы
      // Валидация происходит через dimensionsOrVolumeValidator
      group.get('volumeM3')?.clearValidators();
    } else {
      group.get('lengthMm')?.clearValidators();
      group.get('widthMm')?.clearValidators();
      group.get('heightMm')?.clearValidators();
      // Для режима объема НЕ используем Validators.required - только кастомный валидатор
      // Validators.required может блокировать валидацию
      group.get('volumeM3')?.clearValidators();
    }
    
    group.get('lengthMm')?.updateValueAndValidity({ emitEvent: false });
    group.get('widthMm')?.updateValueAndValidity({ emitEvent: false });
    group.get('heightMm')?.updateValueAndValidity({ emitEvent: false });
    group.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
    
    // Пересчитываем валидацию группы после небольшой задержки, чтобы дать время обновиться значениям
    setTimeout(() => {
      group.updateValueAndValidity();
    }, 0);
  }

  /**
   * Автоматическое вычисление объема из габаритов
   */
  private calculateVolume(group: FormGroup): void {
    const useDimensionsValue = group.get('useDimensions')?.value;
    const useDimensions = Boolean(useDimensionsValue);
    
    if (!useDimensions) {
      return; // Не вычисляем, если используется ручной ввод объема
    }
    
    const length = group.get('lengthMm')?.value;
    const width = group.get('widthMm')?.value;
    const height = group.get('heightMm')?.value;
    
    // Преобразуем в числа для корректной проверки
    const lengthNum = length != null && length !== '' ? Number(length) : null;
    const widthNum = width != null && width !== '' ? Number(width) : null;
    const heightNum = height != null && height !== '' ? Number(height) : null;
    
    // Проверяем, что все три значения заполнены и больше 0
    if (lengthNum != null && widthNum != null && heightNum != null && 
        !isNaN(lengthNum) && !isNaN(widthNum) && !isNaN(heightNum) &&
        lengthNum > 0 && widthNum > 0 && heightNum > 0) {
      // Конвертируем из мм³ в м³: 1 м³ = 1,000,000,000 мм³
      const volumeM3 = (lengthNum * widthNum * heightNum) / 1_000_000_000;
      
      // Для очень маленьких чисел используем больше знаков после запятой
      // Определяем количество знаков в зависимости от величины
      let decimals = 4;
      if (volumeM3 < 0.0001) {
        decimals = 10; // Для очень маленьких чисел используем 10 знаков для точности
      } else if (volumeM3 < 0.001) {
        decimals = 8; // Для маленьких чисел используем 8 знаков
      } else if (volumeM3 < 0.01) {
        decimals = 6; // Для небольших чисел используем 6 знаков
      }
      
      // Используем точное значение без округления для сохранения в форме
      // Округление только для отображения
      const volumeValue = volumeM3;
      
      // Убеждаемся, что значение больше 0 (даже если очень маленькое)
      if (volumeValue > 0) {
        group.get('volumeM3')?.setValue(volumeValue, { emitEvent: false });
        // Принудительно обновляем валидацию после установки значения
        setTimeout(() => {
          group.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
          group.updateValueAndValidity();
        }, 0);
        this.cdr.markForCheck(); // Принудительно обновляем представление
      }
    } else {
      // Если не все габариты заполнены, очищаем объем только если он был вычислен ранее
      // Не трогаем, если пользователь вводил объем вручную
      const currentVolume = group.get('volumeM3')?.value;
      if (currentVolume != null) {
        group.get('volumeM3')?.setValue(null, { emitEvent: false });
        this.cdr.markForCheck();
      }
    }
  }

  /**
   * Получить форму продукта по индексу
   */
  getProductFormGroup(index: number): FormGroup {
    return this.products.at(index) as FormGroup;
  }

  /**
   * Суммарный объем = объём одного места × количество
   */
  getCalculatedTotalVolume(group: FormGroup): number | null {
    const vol = group.get('volumeM3')?.value;
    const qty = group.get('quantity')?.value;
    if (vol == null || vol === '' || Number(vol) <= 0) return null;
    const volNum = Number(vol);
    const qtyNum = (qty != null && qty !== '') ? Math.max(1, Number(qty)) : 1;
    return volNum * qtyNum;
  }

  /**
   * Общий вес = вес одного места × количество
   */
  getCalculatedTotalWeight(group: FormGroup): number | null {
    const w = group.get('weightKg')?.value;
    const qty = group.get('quantity')?.value;
    if (w == null || w === '' || Number(w) <= 0) return null;
    const wNum = Number(w);
    const qtyNum = (qty != null && qty !== '') ? Math.max(1, Number(qty)) : 1;
    return wNum * qtyNum;
  }

  /**
   * Форматировать вычисленный вес для отображения
   */
  formatCalculatedWeight(weight: number | null | undefined): string {
    if (weight == null || weight === 0) return '';
    return Number(weight).toFixed(2).replace(/\.?0+$/, '') || '';
  }

  /**
   * Форматировать вычисленный объем для отображения
   * Использует достаточное количество знаков для очень маленьких чисел
   */
  formatCalculatedVolume(volume: number | null | undefined): string {
    if (volume == null || volume === 0) {
      return '';
    }
    
    // Для очень маленьких чисел используем больше знаков
    if (volume < 0.0001) {
      // Для чисел меньше 0.0001 используем до 10 знаков после запятой
      return volume.toFixed(10).replace(/\.?0+$/, ''); // Убираем лишние нули в конце
    } else if (volume < 0.001) {
      // Для чисел меньше 0.001 используем до 8 знаков
      return volume.toFixed(8).replace(/\.?0+$/, '');
    } else if (volume < 0.01) {
      // Для чисел меньше 0.01 используем до 6 знаков
      return volume.toFixed(6).replace(/\.?0+$/, '');
    } else {
      // Для обычных чисел используем 4 знака
      return volume.toFixed(4).replace(/\.?0+$/, '');
    }
  }

  addProduct(): void {
    this.products.push(this.createProductFormGroup());
  }

  removeProduct(index: number): void {
    if (this.products.length > 1) {
      this.products.removeAt(index);
    }
  }

  createCustomsProductFormGroup(): FormGroup {
    return this.fb.group({
      nameOriginal: ['', Validators.required],
      productCost: [null, [Validators.required, Validators.min(0.01)]],
      currencyCode: ['USD'],
      applicationScope: ['', [Validators.required, Validators.maxLength(300)]],
      id: [null], // Для существующих товаров
      // Поля от AI (readonly после расчета)
      nameForBroker: [null],
      tnvedCodeForBroker: [null],
      customsFees: [null],
      customsDuty: [null],
      vat: [null],
      totalCustomsPayments: [null],
      tnvedCodeInDictionary: [false]
    });
  }

  readonly maxCustomsProducts = 3;

  getCustomsProductCostKzt(product: FormGroup | AbstractControl): number | null {
    const cost = product.get('productCost')?.value;
    const code = product.get('currencyCode')?.value || 'USD';
    if (cost == null || cost === '') return null;
    const rate = this.rates[code] ?? 0;
    return Number(cost) * rate;
  }

  addCustomsProduct(): void {
    if (this.customsProducts.length >= this.maxCustomsProducts) return;
    const newProduct = this.createCustomsProductFormGroup();
    this.customsProducts.push(newProduct);
    
    // Подписываемся на изменения статуса валидности нового товара
    this.subscriptions.add(
      newProduct.statusChanges.subscribe(() => {
        this.cdr.markForCheck();
      })
    );
    
    // Обновляем валидность формы после добавления товара
    this.requestForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  removeCustomsProduct(index: number): void {
    this.customsProducts.removeAt(index);
    // Обновляем валидность формы после удаления товара
    this.requestForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  onCustomsPaymentsToggle(): void {
    const isChecked = this.requestForm.get('calculateCustomsPayments')?.value;
    if (!isChecked) {
      // Если чекбокс выключен, очищаем товары и инвойс
      while (this.customsProducts.length > 0) {
        this.customsProducts.removeAt(0);
      }
      this.invoiceFile = null;
      this.invoiceFileName = '';
      this.invoiceError = null;
    }
  }

  onInvoiceSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.invoiceFile = input.files[0];
      this.invoiceFileName = this.invoiceFile.name;
      this.invoiceError = null;
      // При выборе нового файла сбрасываем флаг существующего, так как новый файл заменит старый
      this.hasExistingInvoice = false;
      this.cdr.markForCheck(); // Обновляем состояние для пересчета canSubmit
    }
  }

  removeInvoice(): void {
    // Если удаляем новый файл, оставляем существующий
    // Если удаляем существующий, сбрасываем флаг
    if (this.invoiceFile) {
      // Удаляем только новый выбранный файл
      this.invoiceFile = null;
      this.invoiceFileName = '';
    } else {
      // Удаляем существующий инвойс
      this.hasExistingInvoice = false;
      this.existingInvoiceFileName = '';
    }
    this.invoiceError = null;
    this.cdr.markForCheck();
  }

  getCargoTypeName(type: CargoTypeDto): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? type.nameEn : type.nameRu;
  }

  getCountryName(country: CountryDto): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? country.nameEn : country.nameRu;
  }

  getCurrencyName(currency: CurrencyDto): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const name = lang === 'en' ? currency.nameEn : currency.nameRu;
    return `${name} (${currency.code})`;
  }

  onSubmitAsDraft(): void {
    this.onSubmit(true);
  }

  onSubmit(asDraft: boolean = false): void {
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      
      // Помечаем все продукты как touched, чтобы показать ошибки
      this.products.controls.forEach((productGroup, index) => {
        if (productGroup instanceof FormGroup) {
          productGroup.markAllAsTouched();
          productGroup.updateValueAndValidity();
        }
      });
      
      // Собираем список незаполненных полей
      const missingFields: string[] = [];
      
      // Проверяем основные поля
      if (this.requestForm.get('rateType')?.invalid) missingFields.push('Тип ставки');
      if (this.requestForm.get('fromCountryId')?.invalid) missingFields.push('Страна отправления');
      if (this.requestForm.get('fromCity')?.invalid) missingFields.push('Город отправления');
      if (this.requestForm.get('toCountryId')?.invalid) missingFields.push('Страна назначения');
      if (this.requestForm.get('toCity')?.invalid) missingFields.push('Город назначения');
      if (this.requestForm.get('loadingDate')?.invalid) missingFields.push('Дата погрузки');
      if (this.requestForm.get('price')?.invalid) missingFields.push('Цена');
      if (this.requestForm.get('currencyId')?.invalid) missingFields.push('Валюта');
      
      // Проверяем продукты
      this.products.controls.forEach((productGroup, index) => {
        if (productGroup instanceof FormGroup) {
          const productNum = index + 1;
          const useDimensions = productGroup.get('useDimensions')?.value;
          if (productGroup.get('cargoTypeId')?.invalid) missingFields.push(`Грузовое место ${productNum}: Тип груза`);
          if (useDimensions) {
            if (productGroup.hasError('weightRequired')) missingFields.push(`Грузовое место ${productNum}: Вес (кг)`);
            if (productGroup.hasError('quantityRequired')) missingFields.push(`Грузовое место ${productNum}: Количество`);
          } else {
            if (productGroup.get('totalWeightKg')?.invalid) missingFields.push(`Грузовое место ${productNum}: Суммарный вес`);
          }
          if (productGroup.hasError('dimensionsRequired')) missingFields.push(`Грузовое место ${productNum}: Габариты (длина, ширина, высота)`);
          if (productGroup.hasError('volumeRequired')) missingFields.push(`Грузовое место ${productNum}: Объем`);
          if (productGroup.hasError('totalWeightRequired')) missingFields.push(`Грузовое место ${productNum}: Суммарный вес`);
        }
      });

      // Проверяем товары для расчета таможенных платежей
      if (this.requestForm.get('calculateCustomsPayments')?.value) {
        if (this.customsProducts.length === 0) {
          missingFields.push('Товары для расчета таможенных платежей: необходимо добавить хотя бы один товар');
        }
        this.customsProducts.controls.forEach((productGroup, index) => {
          if (productGroup instanceof FormGroup) {
            const productNum = index + 1;
            if (productGroup.get('nameOriginal')?.invalid) missingFields.push(`Товар ${productNum}: Наименование товара`);
            if (productGroup.get('productCost')?.invalid) missingFields.push(`Товар ${productNum}: Стоимость товара`);
            if (productGroup.get('applicationScope')?.invalid) missingFields.push(`Товар ${productNum}: Информация о товаре`);
          }
        });
        if (!this.invoiceFile && !this.hasExistingInvoice) {
          missingFields.push('Инвойс: необходимо прикрепить инвойс');
        }
      }
      
      // Показываем детальное сообщение об ошибках
      if (missingFields.length > 0) {
        this.error = `Пожалуйста, заполните следующие поля:\n${missingFields.join(', ')}`;
      } else {
        this.error = 'Пожалуйста, заполните все обязательные поля. Проверьте форму на наличие ошибок.';
      }
      
      // Прокручиваем к первой ошибке
      setTimeout(() => {
        const firstError = document.querySelector('.error-message, .is-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      this.cdr.markForCheck();
      return;
    }

    this.submitting = true;
    this.error = null;
    this.success = false;

    const formValue = this.requestForm.value;
    
    // Обрабатываем продукты: удаляем useDimensions и вычисляем totalWeightKg в режиме габаритов
    const processedProducts = formValue.products.map((product: any) => {
      const useDimensions = Boolean(product.useDimensions);
      const totalWeightKg = useDimensions
        ? (Number(product.weightKg) || 0) * (Number(product.quantity) || 1)
        : Number(product.totalWeightKg) || 0;

      const processed: any = {
        cargoTypeId: product.cargoTypeId,
        name: product.name || undefined,
        tnvedCode: product.tnvedCode || undefined,
        quantity: product.quantity != null ? Number(product.quantity) : undefined,
        weightKg: product.weightKg != null ? Number(product.weightKg) : undefined,
        isDangerous: product.isDangerous,
        isOversized: product.isOversized,
        totalWeightKg,
        isNonStackable: product.isNonStackable
      };

      if (useDimensions) {
        processed.lengthMm = product.lengthMm;
        processed.widthMm = product.widthMm;
        processed.heightMm = product.heightMm;
      } else {
        processed.volumeM3 = product.volumeM3;
      }

      return processed;
    });
    
    const request: CreateCargoRequest = {
      rateType: formValue.rateType,
      fromCountryId: formValue.fromCountryId,
      fromCity: formValue.fromCity.trim(),
      toCountryId: formValue.toCountryId,
      toCity: formValue.toCity.trim(),
      loadingDate: formValue.loadingDate,
      price: formValue.price,
      currencyId: formValue.currencyId,
      calculateCustomsPayments: false,
      isDraft: asDraft,
      products: processedProducts,
      customsCalculationId: this.customsCalculationId
    };

    if (!this.isEditMode && formValue.continueWithOwnForwarder) {
      if (asDraft) {
        this.submitting = false;
        this.error = 'Черновик нельзя создать в режиме продолжения со своим экспедитором.';
        this.cdr.markForCheck();
        return;
      }

      this.subscriptions.add(
        this.cargoRequestService.createCargoRequestWithSystemForwarder(request).subscribe({
          next: (result) => {
            const navigateToDeal = () => {
              this.success = true;
              this.submitting = false;
              this.cdr.markForCheck();
              setTimeout(() => {
                this.router.navigate(['/cargo-owner/deal', result.dealId]);
              }, 800);
            };

            navigateToDeal();
          },
          error: (err) => {
            this.submitting = false;
            if (err.status === 401 || err.status === 403) {
              this.error = 'Ошибка доступа. Пожалуйста, войдите снова.';
            } else if (err.error && err.error.message) {
              this.error = err.error.message;
            } else {
              this.error = 'Ошибка при создании запроса со своим экспедитором';
            }
            this.cdr.markForCheck();
          }
        })
      );
      return;
    }

    const request$ = this.isEditMode && this.requestId
      ? this.cargoRequestService.updateCargoRequest(this.requestId, request)
      : this.cargoRequestService.createCargoRequest(request);

    this.subscriptions.add(
      request$.subscribe({
        next: (createdRequest) => {
          const requestId = createdRequest.id;
          this.requestId = requestId;
          
          this.success = true;
          this.submitting = false;
          setTimeout(() => {
            this.goToDashboardOrProcess();
          }, 1500);
        },
        error: (err) => {
          this.submitting = false;
          if (err.status === 401 || err.status === 403) {
            this.error = 'Ошибка доступа. Пожалуйста, войдите снова.';
          } else if (err.error && err.error.message) {
            this.error = err.error.message;
          } else {
            this.error = this.isEditMode ? 'Ошибка при сохранении изменений' : 'Ошибка при создании запроса';
          }
          this.cdr.markForCheck();
        }
      })
    );
  }

  private saveCustomsProductsAndInvoice(requestId: number, customsProducts: any[]): void {
    if (!customsProducts || customsProducts.length === 0) {
      // Если товаров нет, но это редактирование, удаляем все существующие товары
      if (this.isEditMode && this.existingCustomsProducts.length > 0) {
        const deleteObservables = this.existingCustomsProducts.map(p => 
          this.customsCalculationService.deleteProduct(requestId, p.id)
        );
        forkJoin(deleteObservables).subscribe({
          next: () => {
            this.success = true;
            this.submitting = false;
            setTimeout(() => {
              this.goToDashboardOrProcess();
            }, 1500);
          },
          error: (err) => {
            console.error('Error deleting customs products:', err);
            this.submitting = false;
            this.error = 'Ошибка при удалении товаров: ' + (err.error?.message || 'Неизвестная ошибка');
            this.cdr.markForCheck();
          }
        });
        return;
      }
      
      this.success = true;
      this.submitting = false;
      setTimeout(() => {
        this.goToDashboardOrProcess();
      }, 1500);
      return;
    }

    const productRequests: CreateCustomsProductRequest[] = customsProducts.map(p => ({
      nameOriginal: p.nameOriginal,
      productCost: p.productCost,
      currencyCode: p.currencyCode || 'USD',
      applicationScope: p.applicationScope || undefined
    }));

    if (this.isEditMode && this.existingCustomsProducts.length > 0) {
      // Редактирование: обновляем существующие и создаем новые
      const updateObservables: any[] = [];
      const createObservables: any[] = [];
      const deleteObservables: any[] = [];

      // Находим товары для обновления (по индексу)
      customsProducts.forEach((product, index) => {
        if (index < this.existingCustomsProducts.length) {
          // Обновляем существующий товар
          const existingId = this.existingCustomsProducts[index].id;
          updateObservables.push(
            this.customsCalculationService.updateProductByOwner(requestId, existingId, productRequests[index])
          );
        } else {
          // Создаем новый товар
          createObservables.push(
            this.customsCalculationService.createProduct(requestId, productRequests[index])
          );
        }
      });

      // Удаляем товары, которых больше нет
      if (customsProducts.length < this.existingCustomsProducts.length) {
        for (let i = customsProducts.length; i < this.existingCustomsProducts.length; i++) {
          deleteObservables.push(
            this.customsCalculationService.deleteProduct(requestId, this.existingCustomsProducts[i].id)
          );
        }
      }

      // Выполняем все операции параллельно
      const allObservables = [...updateObservables, ...createObservables, ...deleteObservables];
      if (allObservables.length === 0) {
        // Если нечего обновлять, просто прикрепляем инвойс
        this.uploadInvoiceIfNeeded(requestId);
        return;
      }

      forkJoin(allObservables).subscribe({
        next: (results) => {
          // Обновляем id для новых товаров
          let resultIndex = 0;
          customsProducts.forEach((product, index) => {
            if (index >= this.existingCustomsProducts.length && resultIndex < results.length) {
              const createdProduct = results[resultIndex];
              if (createdProduct && createdProduct.id) {
                const productGroup = this.customsProducts.at(index) as FormGroup;
                if (productGroup) {
                  productGroup.patchValue({ id: createdProduct.id }, { emitEvent: false });
                }
              }
              resultIndex++;
            } else if (index < this.existingCustomsProducts.length) {
              resultIndex++; // Пропускаем результаты обновления
            }
          });
          this.cdr.markForCheck();
          this.uploadInvoiceIfNeeded(requestId);
        },
        error: (err) => {
          console.error('Error updating customs products:', err);
          this.submitting = false;
          this.error = 'Ошибка при обновлении товаров: ' + (err.error?.message || 'Неизвестная ошибка');
          this.cdr.markForCheck();
        }
      });
    } else {
      // Создание: просто создаем все товары
      const createProductObservables = productRequests.map(req =>
        this.customsCalculationService.createProduct(requestId, req)
      );

      forkJoin(createProductObservables).subscribe({
        next: (createdProducts) => {
          // Обновляем id товаров в форме после создания
          createdProducts.forEach((product, index) => {
            if (product && product.id && index < this.customsProducts.length) {
              const productGroup = this.customsProducts.at(index) as FormGroup;
              if (productGroup) {
                productGroup.patchValue({ id: product.id }, { emitEvent: false });
              }
            }
          });
          this.cdr.markForCheck();
          this.uploadInvoiceIfNeeded(requestId);
        },
        error: (err) => {
          console.error('Error creating customs products:', err);
          this.submitting = false;
          this.error = 'Ошибка при создании товаров для расчета таможенных платежей: ' + (err.error?.message || 'Неизвестная ошибка');
          this.cdr.markForCheck();
        }
      });
    }
  }

  private uploadInvoiceIfNeeded(requestId: number): void {
    if (this.invoiceFile) {
      this.fileService.uploadFile(
        this.invoiceFile,
        'CUSTOMS_CALCULATION_INVOICE',
        requestId,
        'INVOICE'
      ).subscribe({
        next: () => {
          this.success = true;
          this.submitting = false;
          // Если режим редактирования, остаемся на странице для возможности использования AI
          if (!this.isEditMode) {
            setTimeout(() => {
              this.goToDashboardOrProcess();
            }, 1500);
          } else {
            // В режиме редактирования обновляем запрос, чтобы получить актуальные данные
            this.loadRequestForEdit(requestId);
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error uploading invoice:', err);
          this.submitting = false;
          this.error = 'Ошибка при прикреплении инвойса: ' + (err.error?.message || 'Неизвестная ошибка');
          this.cdr.markForCheck();
        }
      });
    } else {
      this.success = true;
      this.submitting = false;
      // Если режим редактирования, остаемся на странице для возможности использования AI
      if (!this.isEditMode) {
        setTimeout(() => {
          this.goToDashboardOrProcess();
        }, 1500);
      } else {
        // В режиме редактирования обновляем запрос, чтобы получить актуальные данные
        this.loadRequestForEdit(requestId);
      }
      this.cdr.markForCheck();
    }
  }

  private loadRequestForEdit(id: number): void {
    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: (request) => {
          this.populateForm(request);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loading = false;
          console.error('Error loading request for edit:', err);
          if (err.status === 404) {
            this.error = 'Запрос не найден';
            setTimeout(() => {
              this.goToDashboardOrProcess();
            }, 2000);
          } else if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы.';
          } else {
            this.error = 'Ошибка при загрузке запроса';
          }
          this.cdr.markForCheck();
        }
      })
    );
  }

  private populateForm(request: CargoRequest): void {
    // Форматируем дату для input[type="date"]
    const loadingDate = request.loadingDate ? request.loadingDate.split('T')[0] : '';

    // Убеждаемся, что calculateCustomsPayments устанавливается правильно (не undefined)
    const calculateCustomsPayments = false;

    this.requestForm.patchValue({
      rateType: request.rateType,
      fromCountryId: request.fromCountryId,
      fromCity: request.fromCity,
      toCountryId: request.toCountryId,
      toCity: request.toCity,
      loadingDate: loadingDate,
      price: request.price,
      currencyId: request.currencyId,
      calculateCustomsPayments: calculateCustomsPayments
    });

    // Очищаем массив продуктов и добавляем продукты из запроса
    const productsArray = this.requestForm.get('products') as FormArray;
    productsArray.clear();
    
    request.products.forEach(product => {
      // Определяем, использовать ли габариты или объем
      // Если есть все три габарита И нет объема, то используем габариты
      // Иначе используем объем (даже если габаритов нет)
      const hasDimensions = product.lengthMm && product.widthMm && product.heightMm;
      const useDimensions = hasDimensions && !product.volumeM3;
      
      // Если нет всех габаритов, обязательно выбираем "Заполнить объем"
      // Это важно для правильной валидации при редактировании
      const finalUseDimensions = hasDimensions ? useDimensions : false;
      
      const productGroup = this.fb.group({
        cargoTypeId: [product.cargoTypeId, Validators.required],
        name: [product.name ?? '', Validators.required],
        tnvedCode: [product.tnvedCode ?? '', Validators.required],
        quantity: [product.quantity ?? 1, [Validators.min(1)]],
        weightKg: [product.weightKg ?? (finalUseDimensions ? product.totalWeightKg : null), [Validators.min(0.01)]],
        isDangerous: [product.isDangerous],
        isOversized: [product.isOversized],
        totalWeightKg: [finalUseDimensions ? null : product.totalWeightKg, [Validators.min(0.01)]],
        useDimensions: [finalUseDimensions],
        lengthMm: [product.lengthMm || null, [Validators.min(1)]],
        widthMm: [product.widthMm || null, [Validators.min(1)]],
        heightMm: [product.heightMm || null, [Validators.min(1)]],
        volumeM3: [product.volumeM3 || null],
        isNonStackable: [product.isNonStackable]
      });
      
      // Устанавливаем валидаторы в зависимости от режима
      this.updateDimensionsOrVolumeValidation(productGroup, Boolean(finalUseDimensions));
      productGroup.setValidators(this.dimensionsOrVolumeValidator);
      
      // Подписываемся на изменения для автовычисления объема
      productGroup.get('lengthMm')?.valueChanges.subscribe(() => this.calculateVolume(productGroup));
      productGroup.get('widthMm')?.valueChanges.subscribe(() => this.calculateVolume(productGroup));
      productGroup.get('heightMm')?.valueChanges.subscribe(() => this.calculateVolume(productGroup));
      productGroup.get('quantity')?.valueChanges.subscribe(() => this.cdr.markForCheck());
      productGroup.get('weightKg')?.valueChanges.subscribe(() => this.cdr.markForCheck());
      productGroup.get('useDimensions')?.valueChanges.subscribe((useDims) => {
        // Правильно обрабатываем значение: может быть true, false, "true", "false"
        let useDimsBool: boolean;
        if (useDims === true) {
          useDimsBool = true;
        } else if (useDims === false) {
          useDimsBool = false;
        } else if (typeof useDims === 'string') {
          // Обрабатываем строковые значения
          useDimsBool = useDims === 'true';
        } else {
          useDimsBool = Boolean(useDims);
        }
        
        this.updateDimensionsOrVolumeValidation(productGroup, useDimsBool);

        if (useDimsBool) {
          productGroup.get('volumeM3')?.setValue(null, { emitEvent: false });
          productGroup.get('totalWeightKg')?.setValue(null, { emitEvent: false });
          setTimeout(() => this.calculateVolume(productGroup), 0);
        } else {
          productGroup.get('lengthMm')?.setValue(null, { emitEvent: false });
          productGroup.get('widthMm')?.setValue(null, { emitEvent: false });
          productGroup.get('heightMm')?.setValue(null, { emitEvent: false });
          productGroup.get('weightKg')?.setValue(null, { emitEvent: false });
          productGroup.get('quantity')?.setValue(1, { emitEvent: false });
          setTimeout(() => {
            productGroup.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
            productGroup.get('totalWeightKg')?.updateValueAndValidity({ emitEvent: false });
            productGroup.updateValueAndValidity({ emitEvent: false });
            this.cdr.markForCheck();
          }, 0);
        }
      });
      
      // Подписываемся на изменения volumeM3 для пересчета валидации группы
      productGroup.get('volumeM3')?.valueChanges.subscribe(() => {
        // Пересчитываем валидацию группы при изменении объема
        setTimeout(() => {
          // Сначала обновляем валидацию самого поля
          productGroup.get('volumeM3')?.updateValueAndValidity({ emitEvent: false });
          // Затем обновляем валидацию всей группы (где находится dimensionsOrVolumeValidator)
          productGroup.updateValueAndValidity({ emitEvent: false });
          this.cdr.markForCheck();
        }, 0);
        setTimeout(() => {
          productGroup.updateValueAndValidity({ emitEvent: false });
          this.cdr.markForCheck();
        }, 50);
      });
      
      // Вычисляем объем при инициализации, если габариты уже заполнены
      setTimeout(() => {
        if (Boolean(productGroup.get('useDimensions')?.value)) {
          this.calculateVolume(productGroup);
        }
      }, 0);
      
      productsArray.push(productGroup);
    });

    // Загружаем товары для расчета таможенных платежей, если они есть
    // Используем то же значение, что было установлено выше
    if (calculateCustomsPayments && request.customsProducts) {
      const customsProductsArray = this.requestForm.get('customsProducts') as FormArray;
      customsProductsArray.clear();
      
      // Проверяем, можно ли редактировать товары (только если брокер не начал работу)
      this.canEditCustomsProducts = !request.customsCalculationStarted;
      
      // Сохраняем существующие товары для последующего обновления
      this.existingCustomsProducts = request.customsProducts.map(p => ({ id: p.id }));
      
      request.customsProducts.forEach(product => {
        const productGroup = this.fb.group({
          nameOriginal: [product.nameOriginal, Validators.required],
          productCost: [product.productCost, [Validators.required, Validators.min(0.01)]],
          currencyCode: [product.currencyCode || 'USD'],
          applicationScope: [product.applicationScope || '', [Validators.required, Validators.maxLength(300)]],
          id: [product.id],
          // Поля от AI (если уже рассчитано)
          nameForBroker: [product.nameForBroker || null],
          tnvedCodeForBroker: [product.tnvedCodeForBroker || null],
          customsFees: [product.customsFees || null],
          customsDuty: [product.customsDuty || null],
          vat: [product.vat || null],
          totalCustomsPayments: [product.totalCustomsPayments || null],
          tnvedCodeInDictionary: [product.tnvedCodeInDictionary || false]
        });
        
        // Если товар уже рассчитан через AI, делаем поля readonly
        if (product.nameForBroker) {
          productGroup.get('nameForBroker')?.disable();
          productGroup.get('tnvedCodeForBroker')?.disable();
          productGroup.get('customsFees')?.disable();
          productGroup.get('customsDuty')?.disable();
          productGroup.get('vat')?.disable();
        }
        customsProductsArray.push(productGroup);
        
        // Подписываемся на изменения статуса валидности товара
        this.subscriptions.add(
          productGroup.statusChanges.subscribe(() => {
            this.cdr.markForCheck();
          })
        );
      });

      // Проверяем наличие инвойса на сервере
      if (calculateCustomsPayments && this.isEditMode && request.id) {
        this.fileService.getFilesByContainer('CUSTOMS_CALCULATION_INVOICE', request.id).subscribe({
          next: (files) => {
            this.hasExistingInvoice = files && files.length > 0;
            if (this.hasExistingInvoice && files && files.length > 0) {
              this.existingInvoiceFileName = files[0].fileName || 'Инвойс';
            }
            console.log('Existing invoice check:', { 
              hasExistingInvoice: this.hasExistingInvoice, 
              filesCount: files?.length,
              fileName: this.existingInvoiceFileName
            });
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error checking existing invoice:', err);
            this.hasExistingInvoice = false;
            this.existingInvoiceFileName = '';
            this.cdr.markForCheck();
          }
        });
      } else {
        // Если не требуется расчет таможенных платежей, сбрасываем флаг
        this.hasExistingInvoice = false;
      }
    }
  }

  calculateWithAi(productIndex: number): void {
    if (!this.requestId) {
      this.error = 'Необходимо сначала сохранить запрос';
      this.cdr.markForCheck();
      return;
    }

    const productGroup = this.customsProducts.at(productIndex);
    if (!productGroup) {
      return;
    }

    // Проверяем, что сфера применения заполнена
    const applicationScope = productGroup.get('applicationScope')?.value;
    if (!applicationScope || applicationScope.trim() === '') {
      this.error = 'Необходимо заполнить сферу применения товара';
      this.cdr.markForCheck();
      return;
    }

    const productId = productGroup.get('id')?.value;
    if (!productId) {
      this.error = 'Товар должен быть сохранен перед расчетом через AI';
      this.cdr.markForCheck();
      return;
    }

    this.calculatingWithAi[productIndex] = true;
    this.error = null;
    this.cdr.markForCheck();

    this.customsCalculationService.calculateWithAi(this.requestId, productId, applicationScope).subscribe({
      next: (result) => {
        // Обновляем форму с результатами AI
        productGroup.patchValue({
          nameForBroker: result.nameForBroker,
          tnvedCodeForBroker: result.tnvedCodeForBroker,
          customsFees: result.customsFees,
          customsDuty: result.customsDuty,
          vat: result.vat,
          totalCustomsPayments: result.totalCustomsPayments,
          tnvedCodeInDictionary: result.tnvedCodeInDictionary
        }, { emitEvent: false });

        // Делаем поля брокера readonly (через disable)
        productGroup.get('nameForBroker')?.disable();
        productGroup.get('tnvedCodeForBroker')?.disable();
        productGroup.get('customsFees')?.disable();
        productGroup.get('customsDuty')?.disable();
        productGroup.get('vat')?.disable();

        this.calculatingWithAi[productIndex] = false;
        this.success = true;
        this.cdr.markForCheck();

        setTimeout(() => {
          this.success = false;
          this.cdr.markForCheck();
        }, 3000);
      },
      error: (err) => {
        console.error('Error calculating with AI:', err);
        this.calculatingWithAi[productIndex] = false;
        this.error = 'Ошибка при расчете через AI: ' + (err.error?.message || 'Неизвестная ошибка');
        this.cdr.markForCheck();
      }
    });
  }

  onCancel(): void {
    if (this.customsCalculationId) {
      this.router.navigate(['/calculations', this.customsCalculationId, 'process']);
      return;
    }
    this.router.navigate(['/cargo-owner/dashboard/requests']);
  }

  /** Заявка успешно создана. Если компонент встроен в вкладку «Перевозка» (customsCalculationId
   *  задан) — эмитим событие родителю вместо навигации, он сам покажет просмотр заявки на месте.
   *  Иначе — как раньше, на дашборд. */
  private goToDashboardOrProcess(): void {
    if (this.customsCalculationId) {
      this.requestCreated.emit();
      return;
    }
    this.router.navigate(['/cargo-owner/dashboard/requests']);
  }
}
